import { useState } from 'react';
import API from '@/lib/api';
import { Buildings, Bank, UsersThree, CheckCircle, ArrowRight, ArrowLeft } from '@phosphor-icons/react';

const STEPS = [
  { key: 'bedrijf', label: 'Bedrijfsgegevens', icon: Buildings },
  { key: 'bank', label: 'Bankgegevens', icon: Bank },
  { key: 'klant', label: 'Eerste klant', icon: UsersThree },
  { key: 'klaar', label: 'Klaar!', icon: CheckCircle },
];

export default function OnboardingWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    naam: '', btw: '', email: '', tel: '', adres: '',
    iban: '', bic: '',
    eerste_klant: { bedrijf: '', email: '', btwnr: '' },
  });

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const uKlant = (k) => (e) => setForm(p => ({ ...p, eerste_klant: { ...p.eerste_klant, [k]: e.target.value } }));

  const finish = async () => {
    setSaving(true);
    try {
      await API.post('/onboarding/complete', form);
      onComplete();
    } catch (e) {
      alert('Opslaan mislukt');
    }
    setSaving(false);
  };

  const canNext = () => {
    if (step === 0) return form.naam && form.email;
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" data-testid="onboarding-wizard">
      <div className="bg-white w-full max-w-xl shadow-2xl" style={{ borderTop: '4px solid #002FA7' }}>
        {/* Progress */}
        <div className="flex border-b border-gray-100">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <div key={s.key} className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-semibold transition-all ${
                active ? 'text-[#002FA7] border-b-2 border-[#002FA7]' : done ? 'text-green-600' : 'text-gray-300'
              }`}>
                <Icon size={16} weight={active || done ? 'bold' : 'regular'} />
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            );
          })}
        </div>

        <div className="p-8">
          {/* Step 0: Bedrijf */}
          {step === 0 && (
            <div className="space-y-4" data-testid="onboard-step-bedrijf">
              <h2 className="text-xl font-bold text-[#002FA7]">Welkom bij BillFac</h2>
              <p className="text-sm text-gray-500">Laten we beginnen met uw bedrijfsgegevens. Deze verschijnen op uw facturen.</p>
              <div><label className="form-label">Bedrijfsnaam *</label><input className="field-input" value={form.naam} onChange={u('naam')} placeholder="Uw bedrijf BV" data-testid="onb-naam" /></div>
              <div><label className="form-label">BTW-nummer</label><input className="field-input" value={form.btw} onChange={u('btw')} placeholder="BE 0123.456.789" data-testid="onb-btw" /></div>
              <div><label className="form-label">E-mail *</label><input type="email" className="field-input" value={form.email} onChange={u('email')} placeholder="info@uwbedrijf.be" data-testid="onb-email" /></div>
              <div><label className="form-label">Telefoon</label><input className="field-input" value={form.tel} onChange={u('tel')} placeholder="+32 ..." data-testid="onb-tel" /></div>
              <div><label className="form-label">Adres</label><input className="field-input" value={form.adres} onChange={u('adres')} placeholder="Straat 1, 1000 Brussel" data-testid="onb-adres" /></div>
            </div>
          )}

          {/* Step 1: Bank */}
          {step === 1 && (
            <div className="space-y-4" data-testid="onboard-step-bank">
              <h2 className="text-xl font-bold text-[#002FA7]">Bankgegevens</h2>
              <p className="text-sm text-gray-500">Uw IBAN verschijnt op facturen zodat klanten weten waarheen ze moeten betalen.</p>
              <div><label className="form-label">IBAN</label><input className="field-input font-mono" value={form.iban} onChange={u('iban')} placeholder="BE68 5390 0754 7034" data-testid="onb-iban" /></div>
              <div><label className="form-label">BIC</label><input className="field-input font-mono" value={form.bic} onChange={u('bic')} placeholder="TRIOBEBB" data-testid="onb-bic" /></div>
            </div>
          )}

          {/* Step 2: Eerste klant */}
          {step === 2 && (
            <div className="space-y-4" data-testid="onboard-step-klant">
              <h2 className="text-xl font-bold text-[#002FA7]">Eerste klant aanmaken</h2>
              <p className="text-sm text-gray-500">Optioneel — u kunt dit ook later doen. Maar een eerste klant helpt u snel aan de slag te gaan.</p>
              <div><label className="form-label">Bedrijfsnaam</label><input className="field-input" value={form.eerste_klant.bedrijf} onChange={uKlant('bedrijf')} placeholder="Klant BV" data-testid="onb-klant-naam" /></div>
              <div><label className="form-label">E-mail</label><input type="email" className="field-input" value={form.eerste_klant.email} onChange={uKlant('email')} placeholder="info@klant.be" data-testid="onb-klant-email" /></div>
              <div><label className="form-label">BTW-nummer</label><input className="field-input" value={form.eerste_klant.btwnr} onChange={uKlant('btwnr')} placeholder="BE 0987.654.321" data-testid="onb-klant-btw" /></div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div className="text-center py-8" data-testid="onboard-step-klaar">
              <CheckCircle size={64} weight="bold" className="text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#002FA7] mb-2">Alles klaar!</h2>
              <p className="text-sm text-gray-500 mb-4">Uw account is ingesteld. U kunt nu beginnen met factureren.</p>
              <div className="text-left bg-gray-50 p-4 text-sm space-y-1 max-w-sm mx-auto">
                <div><strong>Bedrijf:</strong> {form.naam}</div>
                <div><strong>E-mail:</strong> {form.email}</div>
                {form.iban && <div><strong>IBAN:</strong> {form.iban}</div>}
                {form.eerste_klant.bedrijf && <div><strong>Eerste klant:</strong> {form.eerste_klant.bedrijf}</div>}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-gray-50">
          {step > 0 && step < 3 ? (
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(s => s - 1)} data-testid="onb-prev">
              <ArrowLeft size={14} /> Vorige
            </button>
          ) : <div />}
          {step < 3 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canNext()} data-testid="onb-next">
              Volgende <ArrowRight size={14} />
            </button>
          ) : (
            <button className="btn btn-primary" onClick={finish} disabled={saving} data-testid="onb-finish">
              {saving ? 'Opslaan...' : 'Start met factureren'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
